import React, { useEffect, useState } from 'react';
import { Box, BoxProps, Button, CircularProgress, InputBase, Stack, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RiSearchLine } from 'react-icons/ri';
import useDebouncedEffect from 'common/hooks/useDebouncedEffect';
import { useTranslation } from 'services/localization';

interface SearchBarProps extends Omit<BoxProps, 'onChange'> {
	onChange: (value: string) => void;
	placeholder?: string;
	defaultValue?: string;
	delay?: number;
	endAdornment?: JSX.Element | null;
}
const SearchBar = (props: SearchBarProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const {
		defaultValue = '',
		onChange,
		placeholder,
		delay = 300,
		endAdornment,
		...boxProps
	} = props;

	const [searchValue, setSearchValue] = useState<string>(defaultValue);
	const [isLoading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!!searchValue) {
			setLoading(true);
		}
	}, [searchValue]);

	useDebouncedEffect(
		() => {
			onChange(searchValue);
			setLoading(false);
		},
		delay,
		[searchValue, onChange],
	);

	return (
		<Wrapper {...boxProps}>
			<Stack direction="row" alignItems="stretch" flex={1} sx={{ minHeight: 40 }}>
				<Box width={40} display="flex" alignItems="center" justifyContent="center">
					{isLoading ? (
						<CircularProgress size={18} />
					) : (
						<RiSearchLine size={18} color={theme.palette.text.secondary} />
					)}
				</Box>
				<InputBase
					sx={{ flex: 1, height: 40 }}
					fullWidth
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					placeholder={placeholder}
					autoFocus
				/>
				{!!searchValue && (
					<Button
						color="primary"
						onClick={() => {
							setSearchValue('');
							onChange('');
						}}
					>
						{t('common:actions.clear', 'Clear')}
					</Button>
				)}
			</Stack>

			<Stack
				direction="row"
				alignItems="center"
				justifyContent="center"
				alignSelf="stretch"
				sx={{
					height: 40,
					borderTop: {
						xs: '1px solid #999999',
						sm: 'none',
					},
					borderLeft: {
						sm: '1px solid #999999',
					},
					'&:empty': {
						display: 'none',
					},
				}}
			>
				{endAdornment}
			</Stack>
		</Wrapper>
	);
};

const Wrapper = styled(Box)(({ theme }) => ({
	background: theme.palette.background.paper,
	border: `1px solid ${theme.palette.border.inputOutline}`,
	borderRadius: '4px',
	height: 40,
	display: 'flex',
	flexDirection: 'row',
	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column',
	},
}));

export default SearchBar;
